import dayjs from "dayjs";

import { captureException } from "~/integrations/posthog/client";
import { supabase } from "~/integrations/supabase/client";
import type {
  BatchCompletionData,
  BatchIngredientOverride,
  ProductionBatch,
  ProductionBatchAccounting,
  ProductionBatchFilters,
  ProductionBatchFormData,
  ProductionBatchIngredient,
  ProductionBatchSummary,
} from "~/types/production";
import {
  generateBatchNumber,
  suggestAlternativeBatchNumber,
} from "~/utils/batchNumberGenerator";

import { recipeService } from "./recipeService";

export const productionBatchService = {
  /**
   * Get all production batches with optional filtering
   */
  async getAll(
    filters?: ProductionBatchFilters
  ): Promise<ProductionBatchSummary[]> {
    let query = supabase
      .from("v_production_batch_summary")
      .select("*")
      .order("created_at", { ascending: false });

    if (filters?.status) {
      if (Array.isArray(filters.status)) {
        query = query.in("status", filters.status);
      } else {
        query = query.eq("status", filters.status);
      }
    }

    if (filters?.product_id) {
      query = query.eq("product_id", filters.product_id);
    }

    if (filters?.start_date_from) {
      query = query.gte("start_date", filters.start_date_from);
    }

    if (filters?.start_date_to) {
      query = query.lte("start_date", filters.start_date_to);
    }

    if (filters?.search) {
      query = query.or(
        `batch_number.ilike.%${filters.search}%,product_name.ilike.%${filters.search}%`
      );
    }

    if (filters?.assembly_line) {
      query = query.eq("assembly_line", filters.assembly_line);
    }

    if (filters?.plant) {
      query = query.eq("plant", filters.plant);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as ProductionBatchSummary[];
  },

  /**
   * Get a single production batch by ID with all details
   */
  async getById(id: string): Promise<ProductionBatch> {
    const { data, error } = await supabase
      .from("production_batches")
      .select(
        `
        *,
        product:products(*),
        recipe:sku_recipes(
          *,
          oil_type:oil_types(*),
          ingredients:recipe_ingredients(*)
        ),
        ingredients:production_batch_ingredients(
          *,
          raw_material:raw_materials(*),
          oil_type:oil_types(*)
        ),
        created_by_profile:profiles!production_batches_created_by_fkey(name, lastname),
        completed_by_profile:profiles!production_batches_completed_by_fkey(name, lastname)
      `
      )
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as ProductionBatch;
  },

  /**
   * Get the real costed unit cost / total (WAC from costing_movements) per batch
   * ingredient, keyed by production_batch_ingredients.id. Only ingredients with a
   * costed consumption movement are returned; uncosted batches map to an empty Map
   * so callers can fall back to the batch-creation snapshot.
   */
  async getIngredientCosts(
    batchId: string
  ): Promise<Map<string, { unitCostClp: number; totalClp: number }>> {
    // get_batch_ingredient_costs was added after the last supabase types
    // regeneration, so call it through a thin typed cast (same approach as
    // facturaCostsService) to keep param/return typing without a full regen.
    const rpcRead = supabase.rpc.bind(supabase) as unknown as (
      fn: string,
      args: Record<string, unknown>
    ) => Promise<{
      data:
        | {
            pbi_id: string;
            costed_total_clp: number;
            costed_unit_cost_clp: number;
          }[]
        | null;
      error: { message: string } | null;
    }>;

    const { data, error } = await rpcRead("get_batch_ingredient_costs", {
      p_batch_id: batchId,
    });
    if (error) throw new Error(error.message);

    const map = new Map<string, { unitCostClp: number; totalClp: number }>();
    for (const row of data ?? []) {
      map.set(row.pbi_id, {
        unitCostClp: Number(row.costed_unit_cost_clp) || 0,
        totalClp: Number(row.costed_total_clp) || 0,
      });
    }
    return map;
  },

  /**
   * Get all existing batch numbers (for uniqueness check)
   */
  async getExistingBatchNumbers(): Promise<string[]> {
    const { data, error } = await supabase
      .from("production_batches")
      .select("batch_number");

    if (error) throw error;
    return (data || []).map((d) => d.batch_number);
  },

  /**
   * Generate a unique batch number for a product
   */
  async generateBatchNumber(
    productCode: string,
    startDate: Date | string,
    endDate: Date | string
  ): Promise<string> {
    const baseBatchNumber = generateBatchNumber(
      productCode,
      startDate,
      endDate
    );
    const existingNumbers = await this.getExistingBatchNumbers();
    return suggestAlternativeBatchNumber(baseBatchNumber, existingNumbers);
  },

  /**
   * Create a new production batch
   * Copies ingredients from recipe and locks current prices
   */
  async create(
    data: ProductionBatchFormData,
    createdBy: string
  ): Promise<ProductionBatch> {
    // Get recipe with ingredients
    let recipe = null;
    if (data.recipe_id) {
      recipe = await recipeService.getById(data.recipe_id);
    }

    // Get current exchange rate
    const { data: exchangeRateData, error: exchangeError } = await supabase
      .from("exchange_rates")
      .select("id, usd_clp, rate_date")
      .order("rate_date", { ascending: false })
      .limit(1)
      .single();

    if (exchangeError) throw exchangeError;

    const exchangeRate = exchangeRateData?.usd_clp || 900;
    const exchangeRateId = exchangeRateData?.id;

    // Get product for batch number generation and unit_code
    const { data: product, error: productError } = await supabase
      .from("products")
      .select("short_name, sku, unit_code")
      .eq("id", data.product_id)
      .single();

    if (productError) throw productError;

    // Generate unique batch number
    const productCode = product.sku || product.short_name || "BATCH";
    const batchNumber = await this.generateBatchNumber(
      productCode,
      data.start_date,
      data.end_date
    );

    // Estimated cost is computed below from the actual ingredient snapshots
    // (buildBatchIngredients → getIngredientPricing) and written in the recalc
    // step after insert. Seed 0 here; a recipe with no ingredients keeps 0.
    const estimatedCostClp = 0;
    const estimatedCostUsd = 0;

    // Generate lot number: DDMMYYYY + assembly_line + plant (if both provided)
    const lotNumber =
      data.assembly_line && data.plant
        ? dayjs(data.start_date).format("DDMMYYYY") +
          data.assembly_line +
          data.plant
        : null;

    // Create the production batch
    const { data: batch, error: batchError } = await supabase
      .from("production_batches")
      .insert({
        batch_number: batchNumber,
        product_id: data.product_id,
        recipe_id: data.recipe_id || null,
        target_quantity: data.target_quantity,
        start_date: data.start_date,
        end_date: data.end_date,
        expiry_date: data.expiry_date,
        status: "planned",
        exchange_rate_id: exchangeRateId,
        exchange_rate_snapshot: exchangeRate,
        estimated_cost_clp: estimatedCostClp,
        estimated_cost_usd: estimatedCostUsd,
        notes: data.notes || null,
        created_by: createdBy,
        assembly_line: data.assembly_line || null,
        plant: data.plant || null,
        lot_number: lotNumber,
        contains_allergens: data.contains_allergens ?? false,
        production_unit: product.unit_code || "CAJA",
      })
      .select()
      .single();

    if (batchError) throw batchError;

    // Build ingredients list from recipe + overrides. On create the base is the
    // recipe itself, so the match key and the FK value are both recipe_ingredients.id.
    const ingredientsToCreate = await this.buildBatchIngredients(
      batch.id,
      (recipe?.ingredients || []).map((ing) => ({
        id: ing.id,
        recipe_ingredient_id: ing.id,
        raw_material_id: ing.raw_material_id,
        oil_type_id: ing.oil_type_id,
        quantity_per_box: ing.quantity_per_box,
        waste_factor: ing.waste_factor,
        notes: ing.notes,
      })),
      data.ingredient_overrides || [],
      data.target_quantity,
      exchangeRate
    );

    if (ingredientsToCreate.length > 0) {
      const { error: ingredientsError } = await supabase
        .from("production_batch_ingredients")
        .insert(ingredientsToCreate);

      if (ingredientsError) throw ingredientsError;

      // Recalculate estimated costs from actual ingredients
      let estimatedCostClpRecalc = 0;
      let estimatedCostUsdRecalc = 0;
      for (const ing of ingredientsToCreate) {
        estimatedCostClpRecalc += ing.unit_cost_clp * ing.planned_quantity;
        if (ing.unit_cost_usd) {
          estimatedCostUsdRecalc += ing.unit_cost_usd * ing.planned_quantity;
        }
      }

      // Update batch with recalculated costs
      const { error: updateCostError } = await supabase
        .from("production_batches")
        .update({
          estimated_cost_clp: estimatedCostClpRecalc,
          estimated_cost_usd: estimatedCostUsdRecalc,
        })
        .eq("id", batch.id);

      if (updateCostError) throw updateCostError;
    }

    return this.getById(batch.id);
  },

  /**
   * Update a production batch (only if status = planned)
   */
  async update(
    id: string,
    data: Partial<ProductionBatchFormData>
  ): Promise<ProductionBatch> {
    const existingBatch = await this.getById(id);

    if (existingBatch.status !== "planned") {
      throw new Error("Solo se pueden modificar lotes en estado planificado");
    }

    const updateData: Record<string, unknown> = {};
    if (data.target_quantity !== undefined)
      updateData.target_quantity = data.target_quantity;
    if (data.start_date !== undefined) updateData.start_date = data.start_date;
    if (data.end_date !== undefined) updateData.end_date = data.end_date;
    if (data.expiry_date !== undefined)
      updateData.expiry_date = data.expiry_date;
    if (data.notes !== undefined) updateData.notes = data.notes || null;
    if (data.contains_allergens !== undefined)
      updateData.contains_allergens = data.contains_allergens;

    // When the target quantity changes, the frozen ingredient snapshots
    // (planned_quantity = qty_per_box * waste_factor * target) and the estimated
    // cost must be rescaled to the new target. Otherwise the completion dialog
    // (which scales planned_quantity by actual/target) miscalculates consumption.
    // Scaling preserves any per-line override proportionally; this only runs on
    // planned batches, so no stock has moved yet.
    const oldTarget = existingBatch.target_quantity;
    const newTarget = data.target_quantity;
    const targetChanged =
      newTarget !== undefined && newTarget !== oldTarget && oldTarget > 0;
    const ratio = targetChanged ? (newTarget as number) / oldTarget : 1;

    if (targetChanged) {
      if (existingBatch.estimated_cost_clp != null)
        updateData.estimated_cost_clp = existingBatch.estimated_cost_clp * ratio;
      if (existingBatch.estimated_cost_usd != null)
        updateData.estimated_cost_usd = existingBatch.estimated_cost_usd * ratio;
    }

    if (Object.keys(updateData).length > 0) {
      const { error } = await supabase
        .from("production_batches")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;
    }

    if (targetChanged && existingBatch.ingredients) {
      for (const ing of existingBatch.ingredients) {
        const { error } = await supabase
          .from("production_batch_ingredients")
          .update({
            planned_quantity: Number((ing.planned_quantity * ratio).toFixed(6)),
          })
          .eq("id", ing.id);

        if (error) throw error;
      }
    }

    return this.getById(id);
  },

  /**
   * Update batch ingredients (only if status = planned)
   */
  async updateIngredient(
    batchId: string,
    ingredientId: string,
    data: { planned_quantity?: number; notes?: string }
  ): Promise<ProductionBatchIngredient> {
    const batch = await this.getById(batchId);

    if (batch.status !== "planned") {
      throw new Error(
        "Solo se pueden modificar ingredientes en lotes planificados"
      );
    }

    const { data: ingredient, error } = await supabase
      .from("production_batch_ingredients")
      .update({
        planned_quantity: data.planned_quantity,
        notes: data.notes || null,
        is_override: true,
      })
      .eq("id", ingredientId)
      .select(
        `
        *,
        raw_material:raw_materials(*),
        oil_type:oil_types(*)
      `
      )
      .single();

    if (error) throw error;
    return ingredient as ProductionBatchIngredient;
  },

  /**
   * Update all batch ingredients (only if status = planned)
   * This replaces all existing ingredients with new ones based on overrides
   */
  async updateIngredients(
    batchId: string,
    overrides: BatchIngredientOverride[],
    targetQuantity: number
  ): Promise<ProductionBatch> {
    const batch = await this.getById(batchId);

    if (batch.status !== "planned") {
      throw new Error(
        "Solo se pueden modificar ingredientes en lotes planificados"
      );
    }

    // Get current exchange rate from the batch
    const exchangeRate = batch.exchange_rate_snapshot || 900;

    // Rebuild from the batch's CURRENT ingredients (what the user edited in the
    // dialog), NOT from the recipe. The edit dialog keys each override by the
    // production_batch_ingredients.id, so the base list must use that same id or
    // every edit is silently discarded and the rows revert to the recipe.
    // Per-box is derived the same way the dialog seeds it (planned_quantity /
    // target, with waste already baked into planned_quantity), which requires
    // this to run while the batch still holds its pre-edit target_quantity.
    const baseTarget = batch.target_quantity || 1;
    const recipeIngredients = (batch.ingredients || []).map((ing) => ({
      id: ing.id, // match key for overrides (the dialog keys by this pbi id)
      recipe_ingredient_id: ing.recipe_ingredient_id, // real FK → recipe_ingredients.id (nullable)
      raw_material_id: ing.raw_material_id,
      oil_type_id: ing.oil_type_id,
      quantity_per_box: ing.planned_quantity / baseTarget,
      waste_factor: 1,
      notes: ing.notes,
    }));

    // Build new ingredients BEFORE deleting, so a build failure can't wipe the batch.
    const ingredientsToCreate = await this.buildBatchIngredients(
      batchId,
      recipeIngredients,
      overrides,
      targetQuantity,
      exchangeRate
    );

    // Replace existing batch ingredients
    const { error: deleteError } = await supabase
      .from("production_batch_ingredients")
      .delete()
      .eq("production_batch_id", batchId);

    if (deleteError) throw deleteError;

    // Insert new ingredients
    if (ingredientsToCreate.length > 0) {
      const { error: insertError } = await supabase
        .from("production_batch_ingredients")
        .insert(ingredientsToCreate);

      if (insertError) throw insertError;
    }

    // Recalculate estimated costs
    let estimatedCostClp = 0;
    let estimatedCostUsd = 0;

    for (const ing of ingredientsToCreate) {
      estimatedCostClp += ing.unit_cost_clp * ing.planned_quantity;
      if (ing.unit_cost_usd) {
        estimatedCostUsd += ing.unit_cost_usd * ing.planned_quantity;
      }
    }

    // Update batch with new estimated costs
    const { error: updateError } = await supabase
      .from("production_batches")
      .update({
        estimated_cost_clp: estimatedCostClp,
        estimated_cost_usd: estimatedCostUsd,
      })
      .eq("id", batchId);

    if (updateError) throw updateError;

    return this.getById(batchId);
  },

  /**
   * Start production (change status to in_progress)
   */
  async start(id: string): Promise<ProductionBatch> {
    const batch = await this.getById(id);

    if (batch.status !== "planned") {
      throw new Error("Solo se pueden iniciar lotes en estado planificado");
    }

    const { error } = await supabase
      .from("production_batches")
      .update({ status: "in_progress" })
      .eq("id", id);

    if (error) throw error;
    return this.getById(id);
  },

  /**
   * Complete a production batch
   * Updates actual quantities, calculates costs, and updates stock
   */
  async complete(
    id: string,
    completionData: BatchCompletionData
  ): Promise<ProductionBatch> {
    const batch = await this.getById(id);

    if (batch.status !== "in_progress") {
      throw new Error("Solo se pueden completar lotes en progreso");
    }

    // 1. Update batch status and actual quantities
    const { error: batchError } = await supabase
      .from("production_batches")
      .update({
        status: "completed",
        actual_quantity: completionData.actual_quantity,
        actual_cost_clp: completionData.actual_cost_clp,
        actual_cost_usd: completionData.actual_cost_usd,
        completed_by: completionData.completed_by,
        notes: completionData.notes || batch.notes,
      })
      .eq("id", id);

    if (batchError) throw batchError;

    // 2. Update actual quantities on batch ingredients
    for (const ingredient of completionData.ingredients) {
      const { error: ingredientError } = await supabase
        .from("production_batch_ingredients")
        .update({ actual_quantity: ingredient.actual_quantity })
        .eq("id", ingredient.id);

      if (ingredientError) throw ingredientError;
    }

    // 3. Deduct oil from stock (skip zero-quantity lines — they would only
    // write noise movements to the ledger)
    const oilIngredients = completionData.ingredients.filter(
      (i) => i.oil_type_id && i.actual_quantity > 0
    );
    for (const oil of oilIngredients) {
      const { error } = await supabase.rpc("decrement_oil_stock", {
        p_oil_type_id: oil.oil_type_id,
        // Oil recipe/batch quantities are in kg; the RPC converts kg -> liters via
        // the oil's density before subtracting from the liters-based stock ledger.
        p_liters: oil.actual_quantity,
        p_plant: batch.plant ?? null,
        p_unit: "kg",
      });

      if (error) {
        console.error("Error decrementing oil stock:", error);
        throw error;
      }
    }

    // 4. Deduct raw materials from stock (skip zero-quantity lines)
    const materialIngredients = completionData.ingredients.filter(
      (i) => i.raw_material_id && i.actual_quantity > 0
    );
    for (const material of materialIngredients) {
      const { error } = await supabase.rpc("decrement_material_stock", {
        p_raw_material_id: material.raw_material_id,
        p_quantity: material.actual_quantity,
        p_plant: batch.plant ?? null,
      });

      if (error) {
        console.error("Error decrementing material stock:", error);
        throw error;
      }
    }

    // 5. Increment finished-product stock. The WMS position/ingestion workflow was
    // retired, so completing a batch now books the produced quantity straight into
    // products.stock — the single working balance shown in Productos. Idempotent per
    // batch (guarded in the RPC) and logs a 'production' stock_movement for the ledger.
    const { error: stockError } = await supabase.rpc(
      "add_production_stock" as never,
      {
        p_batch_id: id,
        p_product_id: batch.product_id,
        p_quantity: completionData.actual_quantity,
        p_created_by: completionData.completed_by ?? null,
      } as never
    );

    if (stockError) {
      // Keep completion resilient (the batch row is already marked completed), but do
      // NOT swallow silently: a failed increment here means the produced quantity never
      // reaches products.stock — the exact bug that let stock drift net-negative while
      // the UI reported success. Surface it loudly so it can't recur unnoticed.
      console.error("Error incrementing product stock:", stockError);
      captureException(
        stockError instanceof Error
          ? stockError
          : new Error(`add_production_stock failed: ${stockError.message}`),
        {
          context: "productionBatchService.complete",
          batch_id: id,
          product_id: batch.product_id,
          actual_quantity: completionData.actual_quantity,
        }
      );
    }

    // 6. Recreate the per-plant un-ingested finished-goods pool row so the completed
    // lote is available for plant-to-plant transfers (Traslado de Producto Terminado).
    // products.stock (step 5) is the aggregate balance; this row is the per-plant/lote
    // availability read by get_product_lotes_for_transfer. GROUPED BY the holding plant
    // of the batch's finished pallets so a pre-close plant transfer survives completion.
    // Idempotent per batch (guarded in the RPC). Does not touch products.stock.
    const { error: ingestionError } = await supabase.rpc(
      "create_batch_ingestion_requests_by_plant" as never,
      {
        p_batch_id: id,
        p_actual_quantity: completionData.actual_quantity,
      } as never
    );

    if (ingestionError) {
      // Non-critical to batch completion — log and continue.
      console.error("Error creating ingestion requests:", ingestionError);
    }

    return this.getById(id);
  },

  /**
   * Update only the expiry (vencimiento) date of a batch that is already in
   * progress or completed. Superadmin-only; enforced server-side by the RPC,
   * which also cascades the new expiry to the batch's pallets and WMS inventory.
   */
  async updateExpiry(id: string, expiryDate: string): Promise<ProductionBatch> {
    const { error } = await supabase.rpc("update_production_batch_expiry", {
      p_batch_id: id,
      p_expiry_date: expiryDate,
    });

    if (error) throw error;
    return this.getById(id);
  },

  /**
   * Cancel a production batch
   */
  async cancel(id: string): Promise<ProductionBatch> {
    const batch = await this.getById(id);

    if (batch.status === "completed") {
      throw new Error("No se pueden cancelar lotes completados");
    }

    const { error } = await supabase
      .from("production_batches")
      .update({ status: "cancelled" })
      .eq("id", id);

    if (error) throw error;
    return this.getById(id);
  },

  /**
   * New-inventory production accounting (Aliace Pruebas test company).
   * Reports whether the batch is eligible (its finished good is a test-company
   * inv product) and its two production asiento ids once contabilized.
   */
  async getAccounting(batchId: string): Promise<ProductionBatchAccounting> {
    const { data, error } = await supabase.rpc(
      "get_production_batch_accounting" as never,
      { p_batch_id: batchId } as never
    );
    if (error) throw error;
    return data as unknown as ProductionBatchAccounting;
  },

  /**
   * Mirror the completed batch into the new inventory ledger (mfg_out consumption
   * + mfg_in finished good) and post the two production asientos:
   *   Dr Productos en Proceso / Cr Materias Primas, then
   *   Dr Productos Terminados / Cr Productos en Proceso.
   * Idempotent and test-company-gated server-side (no-op elsewhere).
   */
  async registerAccounting(
    batchId: string,
    createdBy?: string | null
  ): Promise<ProductionBatchAccounting> {
    const { error } = await supabase.rpc(
      "register_production_batch_accounting" as never,
      { p_batch_id: batchId, p_created_by: createdBy ?? null } as never
    );
    if (error) throw error;
    return this.getAccounting(batchId);
  },

  /**
   * Build batch ingredients from recipe + overrides
   * Handles: unchanged, modified, added, and removed ingredients
   */
  async buildBatchIngredients(
    batchId: string,
    recipeIngredients: {
      id: string; // match key for overrides (recipe_ingredients.id on create, pbi id on edit)
      recipe_ingredient_id: string | null; // value written to the FK column → recipe_ingredients.id
      raw_material_id: string | null;
      oil_type_id: string | null;
      quantity_per_box: number;
      waste_factor: number;
      notes: string | null;
    }[],
    overrides: BatchIngredientOverride[],
    targetQuantity: number,
    exchangeRate: number
  ): Promise<
    {
      production_batch_id: string;
      recipe_ingredient_id: string | null;
      raw_material_id: string | null;
      oil_type_id: string | null;
      planned_quantity: number;
      unit_cost_usd: number | null;
      unit_cost_clp: number;
      exchange_rate_at_batch: number;
      is_override: boolean;
      notes: string | null;
    }[]
  > {
    // Create a map of overrides by recipe_ingredient_id
    const overrideMap = new Map<string | undefined, BatchIngredientOverride>();
    const newIngredients: BatchIngredientOverride[] = [];

    for (const override of overrides) {
      if (override.recipe_ingredient_id) {
        overrideMap.set(override.recipe_ingredient_id, override);
      } else if (!override.is_removed) {
        newIngredients.push(override);
      }
    }

    const batchIngredients: {
      production_batch_id: string;
      recipe_ingredient_id: string | null;
      raw_material_id: string | null;
      oil_type_id: string | null;
      planned_quantity: number;
      unit_cost_usd: number | null;
      unit_cost_clp: number;
      exchange_rate_at_batch: number;
      is_override: boolean;
      notes: string | null;
    }[] = [];

    // Process recipe ingredients (with possible overrides)
    for (const recipeIng of recipeIngredients) {
      const override = overrideMap.get(recipeIng.id);

      // Skip if removed
      if (override?.is_removed) continue;

      // Use override values if present, otherwise use recipe values
      const quantityPerBox =
        override?.quantity_per_box ?? recipeIng.quantity_per_box;
      const wasteFactor = override?.waste_factor ?? recipeIng.waste_factor;
      const oilTypeId = override?.oil_type_id ?? recipeIng.oil_type_id;
      const rawMaterialId =
        override?.raw_material_id ?? recipeIng.raw_material_id;
      const notes = override?.notes ?? recipeIng.notes;

      const plannedQuantity = quantityPerBox * wasteFactor * targetQuantity;
      const isOverride =
        override !== undefined &&
        (override.quantity_per_box !== recipeIng.quantity_per_box ||
          override.waste_factor !== recipeIng.waste_factor);

      // Get pricing
      const pricing = await this.getIngredientPricing(
        oilTypeId,
        rawMaterialId,
        exchangeRate
      );

      batchIngredients.push({
        production_batch_id: batchId,
        recipe_ingredient_id: recipeIng.recipe_ingredient_id,
        raw_material_id: rawMaterialId || null,
        oil_type_id: oilTypeId || null,
        planned_quantity: plannedQuantity,
        unit_cost_usd: pricing.unitCostUsd,
        unit_cost_clp: pricing.unitCostClp,
        exchange_rate_at_batch: exchangeRate,
        is_override: isOverride,
        notes: notes || null,
      });
    }

    // Process new ingredients (not in recipe)
    for (const newIng of newIngredients) {
      const plannedQuantity =
        newIng.quantity_per_box * newIng.waste_factor * targetQuantity;
      const pricing = await this.getIngredientPricing(
        newIng.oil_type_id,
        newIng.raw_material_id,
        exchangeRate
      );

      batchIngredients.push({
        production_batch_id: batchId,
        recipe_ingredient_id: null, // No recipe ingredient - this is new
        raw_material_id: newIng.raw_material_id || null,
        oil_type_id: newIng.oil_type_id || null,
        planned_quantity: plannedQuantity,
        unit_cost_usd: pricing.unitCostUsd,
        unit_cost_clp: pricing.unitCostClp,
        exchange_rate_at_batch: exchangeRate,
        is_override: true, // New ingredients are always overrides
        notes: newIng.notes || null,
      });
    }

    return batchIngredients;
  },

  /**
   * Get current pricing for an ingredient
   * Uses weighted average cost (average_cost_clp) from inventory tables
   */
  async getIngredientPricing(
    oilTypeId: string | undefined | null,
    rawMaterialId: string | undefined | null,
    _exchangeRate: number
  ): Promise<{ unitCostUsd: number | null; unitCostClp: number }> {
    const unitCostUsd: number | null = null;
    let unitCostClp = 0;

    if (oilTypeId) {
      // Oils are stocked in liters, so `average_cost_clp` is a per-LITER cost.
      // Recipe/consumption quantities for oils are in KG, so convert the cost to
      // per-KG (= per-liter / density) before applying it to a kg quantity.
      // Skipping this understates every oil-based finished good by the density factor.
      const { data: oilType } = await supabase
        .from("oil_types")
        .select("average_cost_clp, density_kg_per_liter")
        .eq("id", oilTypeId)
        .single();

      const perLiter = oilType?.average_cost_clp || 0;
      const density = oilType?.density_kg_per_liter || 0.92;
      unitCostClp = density > 0 ? perLiter / density : perLiter;
    } else if (rawMaterialId) {
      // Use weighted average cost from raw_materials table
      const { data: rawMaterial } = await supabase
        .from("raw_materials")
        .select("average_cost_clp")
        .eq("id", rawMaterialId)
        .single();

      unitCostClp = rawMaterial?.average_cost_clp || 0;
    }

    return { unitCostUsd, unitCostClp };
  },

  /**
   * Get batch statistics for dashboard
   */
  async getStatistics(filters?: {
    start_date_from?: string;
    start_date_to?: string;
  }): Promise<{
    total: number;
    planned: number;
    in_progress: number;
    completed: number;
    cancelled: number;
  }> {
    let query = supabase.from("production_batches").select("status");

    if (filters?.start_date_from) {
      query = query.gte("start_date", filters.start_date_from);
    }
    if (filters?.start_date_to) {
      query = query.lte("start_date", filters.start_date_to);
    }

    const { data, error } = await query;

    if (error) throw error;

    const stats = {
      total: data.length,
      planned: 0,
      in_progress: 0,
      completed: 0,
      cancelled: 0,
    };

    for (const batch of data) {
      if (batch.status === "planned") stats.planned++;
      else if (batch.status === "in_progress") stats.in_progress++;
      else if (batch.status === "completed") stats.completed++;
      else if (batch.status === "cancelled") stats.cancelled++;
    }

    return stats;
  },
};
